<template>
  <div class="page-content">
    <div class="title">
      Search settings
    </div>
    <template v-if="!loading">
      <section class="engine" v-for="section in sections" :key="section.title">
        <h2 class="engine-title">{{ section.title }}</h2>
        <p class="engine-description">{{ section.description }}</p>
        <div class="list">
          <div
            class="box"
            v-for="box in section.boxes"
            :key="box.procedureName"
          >
            <template v-if="!isRunning(box.procedureName)">
              <a
                class="btn-dark-button"
                @click="executeProcedure(box.procedureName)"
                >{{ box.label }}</a
              >
              <p v-if="box.description" class="description">
                {{ box.description }}
              </p>
              <template v-if="procedureFor(box.procedureName)">
                <p>
                  Excecuted last time on:
                  {{ procedureFor(box.procedureName).dateFinished }} by
                  {{ procedureFor(box.procedureName).user }}
                </p>
                <p v-if="durationFor(box.procedureName)" class="duration">
                  {{ durationLabelFor(box.procedureName) }}
                  {{ durationFor(box.procedureName) }}
                  <template v-if="processedCountFor(box.procedureName)">
                    for
                    {{ statusFor(box.procedureName).processedProducts }}
                    products<template
                      v-if="
                        statusFor(box.procedureName).changedProducts != null
                      "
                    >
                      ({{
                        statusFor(box.procedureName).changedProducts
                      }}
                      updated)</template
                    >
                  </template>
                </p>
                <template v-if="procedureFor(box.procedureName).success == '1'">
                  <p class="success">
                    Last process was successfull
                  </p>
                </template>
                <template v-else>
                  <p class="error">
                    Last process fail
                  </p>
                  <p v-if="procedureFor(box.procedureName).note" class="note">
                    {{ procedureFor(box.procedureName).note }}
                  </p>
                </template>
              </template>
            </template>
            <template v-else>
              <BaseSmallSpinner />
              {{ box.runningLabel }}
              <p v-if="statusFor(box.procedureName)" class="progress">
                {{ percentageFor(box.procedureName) }}%
                <span class="progress-detail">
                  ({{ statusFor(box.procedureName).processedProducts }} /
                  {{ statusFor(box.procedureName).totalProducts }})
                  <template v-if="statusFor(box.procedureName).changedProducts">
                    — {{ statusFor(box.procedureName).changedProducts }}
                    updated
                  </template>
                </span>
              </p>
              <template>
                <br />
                <br />
                Is this proccess taking too long?
                <a @click="cancelProcess(box.procedureName)">cancel now</a>
              </template>
            </template>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
<script>
import {
  getProcedures,
  requestCancelProcess
} from "@/api/store/ProcedureService.js";
import {
  updateTypesenseAllProducts,
  syncTypesenseDeltaPrice,
  syncTypesenseDeltaFull,
  clearTypesenseIndex,
  syncTypesenseVehicles
} from "@/api/store/ProductsService.js";
const CURRENT_PROCEDURE = "parts";

// Un bloque por motor de búsqueda; el procedure_name de cada caja lo registra
// search-sync. Todas las cajas comparten plantilla y lógica: sólo cambian el
// endpoint que disparan y el nombre del procedimiento.
const SECTIONS = [
  {
    title: "Typesense",
    description:
      "Search server (search.nkrdieselparts.com) used by part-search and the order product search.",
    boxes: [
      {
        procedureName: "typesense_sync_delta_price",
        label: "Sync prices & stock now",
        description:
          "Only products changed by the last Infoauto sync. Runs automatically every hour.",
        runningLabel: "Price sync in progress...",
        request: syncTypesenseDeltaPrice
      },
      {
        procedureName: "typesense_sync_delta_full",
        label: "Sync changed products now",
        description:
          "Compares every field of the whole catalog and sends only what changed. Runs automatically once a day.",
        runningLabel: "Full comparison in progress...",
        request: syncTypesenseDeltaFull
      },
      {
        procedureName: "typesense_refresh",
        label: "Update Typesense index now (full recreate)",
        description:
          "Rebuilds the whole index into a new collection and switches to it when done: search keeps working meanwhile.",
        runningLabel: "Recreate in progress...",
        request: updateTypesenseAllProducts
      },
      {
        procedureName: "typesense_clear_index",
        label: "Clear Typesense index now (delete all products)",
        runningLabel: "DELETE in process...",
        request: clearTypesenseIndex
      },
      {
        procedureName: "typesense_sync_vehicles",
        label: "Sync vehicles now",
        description:
          "Reloads every active vehicle with its family and truck image, used to recognize the vehicle typed in the product search. Runs automatically once a day.",
        runningLabel: "Vehicle sync in progress...",
        request: syncTypesenseVehicles
      }
    ]
  }
];

const ALL_BOXES = SECTIONS.flatMap(section => section.boxes);

export default {
  data() {
    return {
      procedures: [],
      loading: true,
      interval: null
    };
  },
  computed: {
    sections() {
      return SECTIONS;
    },
    isAnyProcedureRunning() {
      return ALL_BOXES.some(box => this.isRunning(box.procedureName));
    }
  },
  mounted() {
    this.getProcedureLogs();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    procedureFor(procedureName) {
      return (
        this.procedures.find(x => x.procedureName == procedureName) || null
      );
    },
    isRunning(procedureName) {
      const procedure = this.procedureFor(procedureName);
      return !!procedure && procedure.dateFinished == null;
    },
    statusFor(procedureName) {
      const procedure = this.procedureFor(procedureName);
      if (!procedure || !procedure.statusJson) {
        return null;
      }
      try {
        return JSON.parse(procedure.statusJson);
      } catch (e) {
        return null;
      }
    },
    // Duración de la última corrida, calculada en el front como
    // dateFinished - dateStart. Si el lambda la reportó en status_json
    // (durationMs) se usa esa, que es más precisa: la mide el propio proceso y
    // no depende de que date_start y date_finished los escriba el mismo motor.
    durationFor(procedureName) {
      const status = this.statusFor(procedureName);
      if (status && status.durationMs) {
        return this.formatDuration(status.durationMs);
      }
      const procedure = this.procedureFor(procedureName);
      if (!procedure || !procedure.dateStart || !procedure.dateFinished) {
        return null;
      }
      // Safari no parsea "2026-09-09 21:28:25" sin la T.
      const toDate = value => new Date(String(value).replace(" ", "T"));
      const elapsed =
        toDate(procedure.dateFinished) - toDate(procedure.dateStart);
      // Solo se descarta lo imposible (fechas invertidas). Una duración enorme
      // suele ser un proceso que quedó colgado y se cerró a mano: ese dato
      // interesa verlo, no esconderlo.
      if (!(elapsed >= 0) || isNaN(elapsed)) {
        return null;
      }
      return this.formatDuration(elapsed);
    },
    // Los procesos que fallaron al arrancar dejan el contador en 0: en ese caso
    // no se muestra el "for N products".
    processedCountFor(procedureName) {
      const status = this.statusFor(procedureName);
      return status ? Number(status.processedProducts) || 0 : 0;
    },
    // Un proceso cancelado o fallido no "tardó" lo que marca el reloj: estuvo
    // abierto ese tiempo. La etiqueta lo distingue para no leer 73 días como si
    // fuese el costo real del sync.
    durationLabelFor(procedureName) {
      const procedure = this.procedureFor(procedureName);
      return procedure && procedure.success == "1" ? "Took" : "Was open for";
    },
    formatDuration(ms) {
      const totalSeconds = Math.round(ms / 1000);
      if (totalSeconds < 1) {
        return "less than 1s";
      }
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (days) {
        return `${days}d ${hours}h`;
      }
      if (hours) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },
    percentageFor(procedureName) {
      const status = this.statusFor(procedureName);
      if (!status || status.totalProducts == null) {
        return null;
      }
      const total = Number(status.totalProducts);
      const processed = Number(status.processedProducts) || 0;
      if (!total) {
        return 0;
      }
      return Math.min(100, Math.round((processed / total) * 100));
    },
    startPolling() {
      if (this.interval) return;
      this.interval = setInterval(() => {
        this.getProcedureLogs();
      }, 5000);
    },
    stopPolling() {
      clearInterval(this.interval);
      this.interval = null;
    },
    async getProcedureLogs() {
      this.procedures = await getProcedures(CURRENT_PROCEDURE);
      this.loading = false;
      // Solo seguimos consultando mientras haya un proceso en curso
      if (this.isAnyProcedureRunning) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    async executeProcedure(procedureName) {
      const box = ALL_BOXES.find(box => box.procedureName == procedureName);
      if (!box) {
        console.log("invalid procedure");
        return;
      }
      box.request();

      this.procedures.unshift({
        procedureName: procedureName,
        dateFinished: null
      });
      this.startPolling();
    },
    cancelProcess(procedureName) {
      requestCancelProcess(CURRENT_PROCEDURE, procedureName);
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/App.scss";
.engine {
  margin-bottom: 30px;
  .engine-title {
    font-size: 20px;
    margin: 10px 10px 0;
  }
  .engine-description {
    font-size: 13px;
    opacity: 0.7;
    margin: 4px 10px 0;
  }
}
.list {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  .box {
    margin: 10px;
    width: 350px;
    padding: 20px;
    @extend .shadow-4dp;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 15px;
    text-align: center;
    position: relative;
    flex-direction: column;
    .options {
      display: none;
    }
    .description {
      font-size: 12px;
      opacity: 0.7;
      margin: 10px 0 0;
    }
    .note {
      font-size: 12px;
      margin: 4px 0 0;
    }
    .duration {
      font-size: 13px;
      font-weight: bold;
      margin: 4px 0 0;
    }
    .progress {
      font-size: 22px;
      font-weight: bold;
      margin: 10px 0 0;
      .progress-detail {
        display: block;
        font-size: 12px;
        font-weight: normal;
        opacity: 0.7;
      }
    }
    a {
      color: red;
    }
    &:hover {
      .options {
        display: block;
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        flex-direction: row;
        .icon {
          border: 1px solid $M8;
          border-radius: 10px;
          padding: 5px;
          width: 30px;
          margin-left: 5px;
          &:hover {
            background-color: $M8;
          }
          img {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
